import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Network, 
  Plus,
  Settings,
  Activity,
  Zap,
  Brain,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Search,
  RefreshCw,
  BarChart3,
  Eye,
  Layers
} from 'lucide-react';
import { buildApiUrl } from '../config';

interface Subnet {
  id: string;
  name: string;
  description: string;
  chainId: number;
  vmType: 'EVM' | 'Custom' | 'AI-Enhanced';
  status: 'active' | 'inactive' | 'deploying' | 'error';
  validators: number;
  totalStake: string;
  blockHeight: number;
  tps: number;
  gasPrice: number;
  aiFeatures: {
    enabled: boolean;
    aiScore: number;
    autoOptimization: boolean;
    smartContracts: number;
  };
  creator: string;
  createdAt: string;
  lastActivity: string;
  networkConfig: {
    blockTime: number;
    gasLimit: number;
    maxValidators: number;
  };
  economics: {
    stakingReward: number;
    transactionFee: number;
    validatorFee: number;
  };
}

interface SubnetTemplate {
  id: string;
  name: string;
  description: string;
  vmType: 'EVM' | 'Custom' | 'AI-Enhanced';
  features: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedCost: string;
  deploymentTime: string;
  aiCapabilities?: {
    smartContracts: boolean;
    autoScaling: boolean;
    predictiveAnalytics: boolean;
    mlIntegration: boolean;
  };
}

interface SubnetStats {
  totalSubnets: number;
  activeSubnets: number;
  totalValidators: number;
  totalStake: string;
  averageTps: number;
  aiEnabledSubnets: number;
}

const SubnetsPage: React.FC = () => {
  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [templates, setTemplates] = useState<SubnetTemplate[]>([]);
  const [stats, setStats] = useState<SubnetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterVmType, setFilterVmType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'subnets' | 'create' | 'templates'>('subnets');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    vmType: 'EVM',
    aiEnabled: false,
    blockTime: 2,
    gasLimit: 15000000,
    maxValidators: 100,
    stakingReward: 10,
    template: ''
  });
  const subnetsPerPage = 12;

  useEffect(() => {
    fetchSubnetData();
    const interval = setInterval(fetchSubnetData, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [filterStatus, filterVmType, sortBy]);

  const fetchSubnetData = async () => {
    try {
      setLoading(true);
      
      // 실제 서브넷 팩토리 API 호출
      let subnetsResponse: Response | null = null;
      let templatesResponse: Response | null = null;
      
      try {
        subnetsResponse = await fetch(buildApiUrl('/subnets'));
      } catch (e) {
        subnetsResponse = null;
      }
      
      try {
        templatesResponse = await fetch(buildApiUrl('/subnets/templates'));
      } catch (e) {
        templatesResponse = null;
      }
      
      let realSubnets: Subnet[] = [];
      let realTemplates: SubnetTemplate[] = [];
      
      if (subnetsResponse && subnetsResponse.ok) {
        const data = await subnetsResponse.json();
        realSubnets = data.subnets || [];
      }
      
      if (templatesResponse && templatesResponse.ok) {
        const data = await templatesResponse.json();
        realTemplates = data.templates || [];
      }
      
      // Mock 서브넷 데이터 (실제 데이터가 없을 때 표시용)
      const mockSubnets: Subnet[] = [
        {
          id: 'subnet-1',
          name: 'Voltron AI Subnet',
          description: 'AI-enhanced subnet for machine learning and smart contract optimization',
          chainId: 88801,
          vmType: 'AI-Enhanced',
          status: 'active',
          validators: 25,
          totalStake: '2500000',
          blockHeight: 1234567,
          tps: 3500,
          gasPrice: 15.5,
          aiFeatures: {
            enabled: true,
            aiScore: 95.8,
            autoOptimization: true,
            smartContracts: 156
          },
          creator: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
          createdAt: '2024-01-15T00:00:00Z',
          lastActivity: new Date().toISOString(),
          networkConfig: {
            blockTime: 1.5,
            gasLimit: 20000000,
            maxValidators: 50
          },
          economics: {
            stakingReward: 12.5,
            transactionFee: 0.001,
            validatorFee: 5
          }
        },
        {
          id: 'subnet-2',
          name: 'VOLTRON Energy Network',
          description: 'Specialized subnet for energy trading and carbon credit management',
          chainId: 88802,
          vmType: 'EVM',
          status: 'active',
          validators: 18,
          totalStake: '1800000',
          blockHeight: 987654,
          tps: 2100,
          gasPrice: 20.0,
          aiFeatures: {
            enabled: false,
            aiScore: 0,
            autoOptimization: false,
            smartContracts: 89
          },
          creator: '0x853e46Dd7645D1542936a4c5E5D3D3D3D3D3D3D3',
          createdAt: '2024-02-01T00:00:00Z',
          lastActivity: new Date(Date.now() - 300000).toISOString(),
          networkConfig: {
            blockTime: 2.0,
            gasLimit: 15000000,
            maxValidators: 30
          },
          economics: {
            stakingReward: 10.0,
            transactionFee: 0.002,
            validatorFee: 7
          }
        },
        {
          id: 'subnet-3',
          name: 'BSS Gaming Subnet',
          description: 'High-performance subnet optimized for gaming and NFT applications',
          chainId: 88803,
          vmType: 'Custom',
          status: 'active',
          validators: 12,
          totalStake: '1200000',
          blockHeight: 654321,
          tps: 5000,
          gasPrice: 10.0,
          aiFeatures: {
            enabled: true,
            aiScore: 78.3,
            autoOptimization: false,
            smartContracts: 234
          },
          creator: '0x964f57Ee8756E2653047b5c6F6E4E4E4E4E4E4E4',
          createdAt: '2024-02-15T00:00:00Z',
          lastActivity: new Date(Date.now() - 600000).toISOString(),
          networkConfig: {
            blockTime: 1.0,
            gasLimit: 25000000,
            maxValidators: 20
          },
          economics: {
            stakingReward: 15.0,
            transactionFee: 0.0005,
            validatorFee: 3
          }
        },
        {
          id: 'subnet-4',
          name: 'DeFi Protocol Subnet',
          description: 'Dedicated subnet for decentralized finance protocols and yield farming',
          chainId: 88804,
          vmType: 'EVM',
          status: 'deploying',
          validators: 0,
          totalStake: '0',
          blockHeight: 0,
          tps: 0,
          gasPrice: 25.0,
          aiFeatures: {
            enabled: true,
            aiScore: 0,
            autoOptimization: true,
            smartContracts: 0
          },
          creator: '0x123456789012345678901234567890123456789',
          createdAt: '2024-03-01T00:00:00Z',
          lastActivity: new Date(Date.now() - 1800000).toISOString(),
          networkConfig: {
            blockTime: 2.5,
            gasLimit: 18000000,
            maxValidators: 40
          },
          economics: {
            stakingReward: 8.0,
            transactionFee: 0.003,
            validatorFee: 6
          }
        }
      ];
      
      // 추가 서브넷들 생성
      for (let i = 0; i < 8; i++) {
        const vmTypes = ['EVM', 'Custom', 'AI-Enhanced'];
        const statuses = ['active', 'inactive', 'deploying', 'error'];
        
        mockSubnets.push({
          id: `subnet-${i + 5}`,
          name: `Subnet ${i + 5}`,
          description: `Custom subnet for specialized use case ${i + 5}`,
          chainId: 88805 + i,
          vmType: vmTypes[Math.floor(Math.random() * vmTypes.length)] as 'EVM' | 'Custom' | 'AI-Enhanced',
          status: statuses[Math.floor(Math.random() * statuses.length)] as 'active' | 'inactive' | 'deploying' | 'error',
          validators: Math.floor(Math.random() * 30) + 5,
          totalStake: (Math.random() * 2000000 + 100000).toFixed(0),
          blockHeight: Math.floor(Math.random() * 1000000) + 100000,
          tps: Math.floor(Math.random() * 4000) + 1000,
          gasPrice: Math.random() * 30 + 10,
          aiFeatures: {
            enabled: Math.random() > 0.5,
            aiScore: Math.random() * 100,
            autoOptimization: Math.random() > 0.7,
            smartContracts: Math.floor(Math.random() * 200)
          },
          creator: `0x${Math.random().toString(16).substring(2, 18)}${Math.random().toString(16).substring(2, 18)}${Math.random().toString(16).substring(2, 10)}`,
          createdAt: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(),
          lastActivity: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
          networkConfig: {
            blockTime: Math.random() * 3 + 1,
            gasLimit: Math.floor(Math.random() * 10000000) + 10000000,
            maxValidators: Math.floor(Math.random() * 50) + 20
          },
          economics: {
            stakingReward: Math.random() * 10 + 5,
            transactionFee: Math.random() * 0.005,
            validatorFee: Math.random() * 5 + 2
          }
        });
      }
      
      // Mock 템플릿 데이터
      const mockTemplates: SubnetTemplate[] = [
        {
          id: 'template-1',
          name: 'AI-Enhanced EVM',
          description: 'EVM-compatible subnet with integrated AI capabilities for smart contract optimization',
          vmType: 'AI-Enhanced',
          features: ['AI Optimization', 'Smart Contract Analysis', 'Predictive Scaling', 'Auto Gas Optimization'],
          difficulty: 'Advanced',
          estimatedCost: '50,000 VTM',
          deploymentTime: '15-30 minutes',
          aiCapabilities: {
            smartContracts: true,
            autoScaling: true,
            predictiveAnalytics: true,
            mlIntegration: true
          }
        },
        {
          id: 'template-2',
          name: 'Standard EVM',
          description: 'Basic EVM-compatible subnet for general-purpose applications',
          vmType: 'EVM',
          features: ['EVM Compatibility', 'Standard Gas Model', 'Basic Governance', 'Multi-Sig Support'],
          difficulty: 'Beginner',
          estimatedCost: '10,000 VTM',
          deploymentTime: '5-10 minutes'
        },
        {
          id: 'template-3',
          name: 'Gaming Optimized',
          description: 'High-performance subnet optimized for gaming and real-time applications',
          vmType: 'Custom',
          features: ['Ultra-Fast Blocks', 'Low Latency', 'NFT Optimization', 'Gaming APIs'],
          difficulty: 'Intermediate',
          estimatedCost: '25,000 VTM',
          deploymentTime: '10-20 minutes'
        },
        {
          id: 'template-4',
          name: 'DeFi Protocol',
          description: 'Specialized subnet for DeFi applications with enhanced security',
          vmType: 'EVM',
          features: ['DeFi Primitives', 'Oracle Integration', 'Yield Farming', 'Liquidity Pools'],
          difficulty: 'Intermediate',
          estimatedCost: '30,000 VTM',
          deploymentTime: '15-25 minutes'
        },
        {
          id: 'template-5',
          name: 'Enterprise Private',
          description: 'Private subnet for enterprise applications with advanced permissions',
          vmType: 'Custom',
          features: ['Private Network', 'Permission Controls', 'Enterprise APIs', 'Compliance Tools'],
          difficulty: 'Advanced',
          estimatedCost: '75,000 VTM',
          deploymentTime: '30-45 minutes'
        }
      ];
      
      // 필터링
      let filteredSubnets = mockSubnets;
      
      if (filterStatus !== 'all') {
        filteredSubnets = filteredSubnets.filter(subnet => subnet.status === filterStatus);
      }
      
      if (filterVmType !== 'all') {
        filteredSubnets = filteredSubnets.filter(subnet => subnet.vmType === filterVmType);
      }
      
      if (searchTerm) {
        filteredSubnets = filteredSubnets.filter(subnet => 
          subnet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          subnet.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          subnet.chainId.toString().includes(searchTerm)
        );
      }
      
      // 정렬
      filteredSubnets.sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          case 'oldest':
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          case 'validators':
            return b.validators - a.validators;
          case 'stake':
            return parseFloat(b.totalStake) - parseFloat(a.totalStake);
          case 'tps':
            return b.tps - a.tps;
          default:
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
      });
      
      // 실제 API 데이터와 Mock 데이터 결합
      const combinedSubnets = [...realSubnets, ...filteredSubnets];
      const combinedTemplates = [...realTemplates, ...mockTemplates];
      setSubnets(combinedSubnets);
      setTemplates(combinedTemplates);
      
      // 통계 계산
      const totalSubnets = filteredSubnets.length;
      const activeSubnets = filteredSubnets.filter(s => s.status === 'active').length;
      const totalValidators = filteredSubnets.reduce((sum, s) => sum + s.validators, 0);
      const totalStake = filteredSubnets.reduce((sum, s) => sum + parseFloat(s.totalStake), 0);
      const averageTps = filteredSubnets.reduce((sum, s) => sum + s.tps, 0) / filteredSubnets.length;
      const aiEnabledSubnets = filteredSubnets.filter(s => s.aiFeatures.enabled).length;
      
      setStats({
        totalSubnets,
        activeSubnets,
        totalValidators,
        totalStake: totalStake.toFixed(0),
        averageTps: averageTps || 0,
        aiEnabledSubnets
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch subnet data:', error);
      setLoading(false);
    }
  };

  // 서브넷 상세 정보 조회
  const fetchSubnetDetails = async (subnetId: string) => {
    try {
      const response = await fetch(buildApiUrl(`/subnets/${subnetId}`));
      if (response.ok) {
        const data = await response.json();
        return data.subnet;
      }
    } catch (error) {
      console.error('Failed to fetch subnet details:', error);
    }
    return null;
  };

  // 서브넷 중지
  const handleStopSubnet = async (subnetId: string) => {
    try {
      const response = await fetch(buildApiUrl(`/subnets/${subnetId}/stop`), {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('서브넷이 성공적으로 중지되었습니다.');
        await fetchSubnetData();
      } else {
        const error = await response.json();
        alert(`서브넷 중지 실패: ${error.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('Failed to stop subnet:', error);
      alert('서브넷 중지 중 오류가 발생했습니다.');
    }
  };

  // 서브넷 재시작
  const handleRestartSubnet = async (subnetId: string) => {
    try {
      const response = await fetch(buildApiUrl(`/subnets/${subnetId}/restart`), {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('서브넷이 성공적으로 재시작되었습니다.');
        await fetchSubnetData();
      } else {
        const error = await response.json();
        alert(`서브넷 재시작 실패: ${error.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('Failed to restart subnet:', error);
      alert('서브넷 재시작 중 오류가 발생했습니다.');
    }
  };

  // 서브넷 삭제
  const handleDeleteSubnet = async (subnetId: string) => {
    if (!window.confirm('정말로 이 서브넷을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      const response = await fetch(buildApiUrl(`/subnets/${subnetId}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        alert('서브넷이 성공적으로 삭제되었습니다.');
        await fetchSubnetData();
      } else {
        const error = await response.json();
        alert(`서브넷 삭제 실패: ${error.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('Failed to delete subnet:', error);
      alert('서브넷 삭제 중 오류가 발생했습니다.');
    }
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toFixed(0);
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      case 'deploying':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'deploying': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVmTypeColor = (vmType: string) => {
    switch (vmType) {
      case 'EVM': return 'bg-blue-100 text-blue-800';
      case 'Custom': return 'bg-purple-100 text-purple-800';
      case 'AI-Enhanced': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const handleCreateSubnet = async () => {
    try {
      setLoading(true);
      
      // 실제 서브넷 팩토리 API 호출
      const subnetConfig = {
        name: createForm.name,
        symbol: createForm.name.toUpperCase().substring(0, 4),
        template: createForm.template || 'voltron',
        vmType: createForm.vmType,
        aiEnabled: createForm.aiEnabled,
        validators: 3,
        resources: {
          cpu: '2',
          memory: '4g',
          storage: '100g'
        },
        networkConfig: {
          blockTime: createForm.blockTime,
          gasLimit: createForm.gasLimit,
          maxValidators: createForm.maxValidators
        },
        economics: {
          stakingReward: createForm.stakingReward
        }
      };
      
      const response = await fetch(buildApiUrl('/subnets/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subnetConfig)
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`서브넷이 성공적으로 생성되었습니다!\n배포 ID: ${result.deploymentId || 'N/A'}\n체인 ID: ${result.chainId || 'N/A'}`);
        
        // 서브넷 목록 새로고침
        await fetchSubnetData();
      } else {
        const error = await response.json();
        alert(`서브넷 생성 실패: ${error.message || '알 수 없는 오류'}`);
      }
      
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        description: '',
        vmType: 'EVM',
        aiEnabled: false,
        blockTime: 2,
        gasLimit: 15000000,
        maxValidators: 100,
        stakingReward: 10,
        template: ''
      });
    } catch (error) {
      console.error('서브넷 생성 중 오류:', error);
      alert(`서브넷 생성 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  const paginatedSubnets = subnets.slice(
    (currentPage - 1) * subnetsPerPage,
    currentPage * subnetsPerPage
  );

  const totalPages = Math.ceil(subnets.length / subnetsPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Subnet Factory</h1>
          <p className="text-slate-600">Create and manage custom blockchain subnets with AI capabilities</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchSubnetData}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Subnet</span>
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Subnets</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSubnets}</p>
              </div>
              <Network className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Subnets</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeSubnets}</p>
              </div>
              <Activity className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Validators</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalValidators}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Stake</p>
                <p className="text-2xl font-bold text-blue-600">{formatNumber(stats.totalStake)} VTM</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average TPS</p>
                <p className="text-2xl font-bold text-indigo-600">{formatNumber(stats.averageTps)}</p>
              </div>
              <Zap className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">AI-Enabled</p>
                <p className="text-2xl font-bold text-pink-600">{stats.aiEnabledSubnets}</p>
              </div>
              <Brain className="w-8 h-8 text-pink-500" />
            </div>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('subnets')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'subnets'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All Subnets
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'create'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Create Custom
          </button>
        </div>
      </div>

      {activeTab === 'subnets' && (
        <>
          {/* 검색 및 필터 */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search subnets by name, description, or chain ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deploying">Deploying</option>
                <option value="error">Error</option>
              </select>
              
              <select
                value={filterVmType}
                onChange={(e) => setFilterVmType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All VM Types</option>
                <option value="EVM">EVM</option>
                <option value="Custom">Custom</option>
                <option value="AI-Enhanced">AI-Enhanced</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="validators">Validators</option>
                <option value="stake">Total Stake</option>
                <option value="tps">TPS</option>
              </select>
            </div>
          </div>

          {/* 서브넷 그리드 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : paginatedSubnets.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                <Network className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No subnets found</p>
                <p className="text-sm">Create your first subnet to get started!</p>
              </div>
            ) : (
              paginatedSubnets.map((subnet) => (
                <div key={subnet.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{subnet.name}</h3>
                        {subnet.aiFeatures.enabled && (
                          <Brain className="w-4 h-4 text-pink-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{subnet.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(subnet.status)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subnet.status)}`}>
                        {subnet.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Chain ID</span>
                      <span className="text-sm font-medium text-gray-900">{subnet.chainId}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">VM Type</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVmTypeColor(subnet.vmType)}`}>
                        {subnet.vmType}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Validators</span>
                      <span className="text-sm font-medium text-gray-900">{subnet.validators}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Stake</span>
                      <span className="text-sm font-medium text-gray-900">{formatNumber(subnet.totalStake)} VTM</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">TPS</span>
                      <span className="text-sm font-medium text-gray-900">{subnet.tps.toLocaleString()}</span>
                    </div>
                    
                    {subnet.aiFeatures.enabled && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">AI Score</span>
                        <span className="text-sm font-medium text-pink-600">{subnet.aiFeatures.aiScore.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500">
                      Created {formatTimeAgo(subnet.createdAt)}
                    </span>
                    <span className="text-xs text-gray-500">
                      Active {formatTimeAgo(subnet.lastActivity)}
                    </span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => fetchSubnetDetails(subnet.id)}
                      className="flex-1 py-2 px-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View
                    </button>
                    
                    {subnet.status === 'active' ? (
                      <button 
                        onClick={() => handleStopSubnet(subnet.id)}
                        className="flex-1 py-2 px-3 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <XCircle className="w-4 h-4 inline mr-1" />
                        Stop
                      </button>
                    ) : subnet.status === 'inactive' ? (
                      <button 
                        onClick={() => handleRestartSubnet(subnet.id)}
                        className="flex-1 py-2 px-3 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Start
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleDeleteSubnet(subnet.id)}
                        className="flex-1 py-2 px-3 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <Settings className="w-4 h-4 inline mr-1" />
                        Manage
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-lg ${
                      currentPage === page
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVmTypeColor(template.vmType)}`}>
                  {template.vmType}
                </span>
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Difficulty</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyColor(template.difficulty)}`}>
                    {template.difficulty}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Estimated Cost</span>
                  <span className="text-sm font-medium text-gray-900">{template.estimatedCost}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Deploy Time</span>
                  <span className="text-sm font-medium text-gray-900">{template.deploymentTime}</span>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Features</h4>
                <div className="flex flex-wrap gap-1">
                  {template.features.map((feature, index) => (
                    <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
              
              {template.aiCapabilities && (
                <div className="mb-4 p-3 bg-pink-50 rounded-lg">
                  <h4 className="text-sm font-medium text-pink-900 mb-2 flex items-center">
                    <Brain className="w-4 h-4 mr-1" />
                    AI Capabilities
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`flex items-center ${template.aiCapabilities.smartContracts ? 'text-green-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Smart Contracts
                    </div>
                    <div className={`flex items-center ${template.aiCapabilities.autoScaling ? 'text-green-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Auto Scaling
                    </div>
                    <div className={`flex items-center ${template.aiCapabilities.predictiveAnalytics ? 'text-green-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Predictive Analytics
                    </div>
                    <div className={`flex items-center ${template.aiCapabilities.mlIntegration ? 'text-green-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      ML Integration
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => {
                  setCreateForm({...createForm, template: template.id, vmType: template.vmType, aiEnabled: !!template.aiCapabilities});
                  setActiveTab('create');
                }}
                className="w-full py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Use Template
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-orange-500" />
            Create Custom Subnet
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subnet Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="My Custom Subnet"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Describe your subnet's purpose and features..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
                <select
                  value={createForm.template}
                  onChange={(e) => {
                    const selectedTemplate = templates.find(t => t.id === e.target.value);
                    setCreateForm({
                      ...createForm, 
                      template: e.target.value,
                      vmType: selectedTemplate?.vmType || 'EVM',
                      aiEnabled: selectedTemplate?.aiCapabilities?.mlIntegration || false
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select a template...</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.difficulty} ({template.estimatedCost})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">VM Type</label>
                <select
                  value={createForm.vmType}
                  onChange={(e) => setCreateForm({...createForm, vmType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="EVM">EVM Compatible</option>
                  <option value="Custom">Custom VM</option>
                  <option value="AI-Enhanced">AI-Enhanced EVM</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="aiEnabled"
                  checked={createForm.aiEnabled}
                  onChange={(e) => setCreateForm({...createForm, aiEnabled: e.target.checked})}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="aiEnabled" className="text-sm font-medium text-gray-700 flex items-center">
                  <Brain className="w-4 h-4 mr-1 text-pink-500" />
                  Enable AI Features
                </label>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Block Time (seconds)</label>
                <input
                  type="number"
                  value={createForm.blockTime}
                  onChange={(e) => setCreateForm({...createForm, blockTime: parseFloat(e.target.value)})}
                  min="0.5"
                  max="10"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gas Limit</label>
                <input
                  type="number"
                  value={createForm.gasLimit}
                  onChange={(e) => setCreateForm({...createForm, gasLimit: parseInt(e.target.value)})}
                  min="1000000"
                  max="50000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Validators</label>
                <input
                  type="number"
                  value={createForm.maxValidators}
                  onChange={(e) => setCreateForm({...createForm, maxValidators: parseInt(e.target.value)})}
                  min="5"
                  max="200"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Staking Reward (%)</label>
                <input
                  type="number"
                  value={createForm.stakingReward}
                  onChange={(e) => setCreateForm({...createForm, stakingReward: parseFloat(e.target.value)})}
                  min="1"
                  max="20"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Estimated Costs</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700">Deployment Fee:</span>
                <span className="font-medium text-blue-900 ml-2">25,000 VTM</span>
              </div>
              <div>
                <span className="text-blue-700">Monthly Operating:</span>
                <span className="font-medium text-blue-900 ml-2">5,000 VTM</span>
              </div>
              {createForm.aiEnabled && (
                <div>
                  <span className="text-blue-700">AI Features:</span>
                  <span className="font-medium text-blue-900 ml-2">+10,000 VTM</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 flex space-x-4">
            <button
              onClick={handleCreateSubnet}
              disabled={!createForm.name || !createForm.description}
              className="flex-1 py-3 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Deploy Subnet</span>
            </button>
            <button
              onClick={() => setCreateForm({
                name: '',
                description: '',
                vmType: 'EVM',
                aiEnabled: false,
                blockTime: 2,
                gasLimit: 15000000,
                maxValidators: 100,
                stakingReward: 10,
                template: ''
              })}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Subnet Creation</h3>
            <p className="text-sm text-gray-600 mb-6">
              Choose how you'd like to create your subnet:
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setActiveTab('templates');
                  setShowCreateModal(false);
                }}
                className="w-full p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Layers className="w-5 h-5 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">Use Template</div>
                    <div className="text-sm text-gray-500">Start with pre-configured templates</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('create');
                  setShowCreateModal(false);
                }}
                className="w-full p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Settings className="w-5 h-5 text-orange-500" />
                  <div>
                    <div className="font-medium text-gray-900">Custom Configuration</div>
                    <div className="text-sm text-gray-500">Configure all parameters manually</div>
                  </div>
                </div>
              </button>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SubnetsPage;
